import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSettings } from '@/contexts/SettingsContext';
import { getNavItemsForRole } from '@/lib/permissions';
import {
  ChevronLeft, ChevronRight,
  LogOut, Sun, Moon, X
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { user, logout, userPermissions, isAdminPermissions } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const navItems = getNavItemsForRole(user?.role, userPermissions, isAdminPermissions);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNav = () => {
    if (mobileOpen) onMobileClose();
  };

  const isCollapsed = collapsed && !mobileOpen;

  return (
    <TooltipProvider delayDuration={0}>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-[2px]" onClick={onMobileClose} />
      )}

      <aside
        data-testid="sidebar"
        className={`
          fixed left-0 top-0 h-full z-50 flex flex-col border-r border-border transition-all duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${isCollapsed ? 'md:w-[68px]' : 'w-[260px] md:w-[240px]'}
        `}
        style={{ background: 'hsl(var(--sidebar-bg))' }}
      >
        {/* Logo */}
        <div className={`flex items-center h-14 md:h-16 px-4 border-b border-border ${isCollapsed ? 'md:justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-8 h-8 rounded-md flex-shrink-0" />
            {!isCollapsed && <span className="font-semibold text-foreground tracking-tight text-[15px]">{settings.company_name || 'Zynq'}</span>}
          </div>
          {mobileOpen && (
            <button onClick={onMobileClose} className="md:hidden text-muted-foreground hover:text-foreground p-1">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            if (isCollapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      onClick={handleNav}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        }`
                      }
                    >
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="font-medium">{item.label}</TooltipContent>
                </Tooltip>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={handleNav}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-border p-2 space-y-1">
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle"
                  className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  {theme === 'dark' ? <Sun className="w-[18px] h-[18px] flex-shrink-0" /> : <Moon className="w-[18px] h-[18px] flex-shrink-0" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{theme === 'dark' ? 'Licht' : 'Donker'}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-[18px] h-[18px] flex-shrink-0" /> : <Moon className="w-[18px] h-[18px] flex-shrink-0" />}
              <span>{theme === 'dark' ? 'Lichte modus' : 'Donkere modus'}</span>
            </button>
          )}

          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onToggle}
                  data-testid="sidebar-toggle"
                  className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Uitklappen</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={onToggle}
              data-testid="sidebar-toggle"
              className="hidden md:flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-[18px] h-[18px] flex-shrink-0" />
              <span>Inklappen</span>
            </button>
          )}

          {!isCollapsed && user && (
            <div className="flex items-center gap-3 px-3 py-2 mt-1">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
                {user.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user.role_name || user.role}</p>
              </div>
              <button onClick={handleLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          {isCollapsed && user && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleLogout} data-testid="logout-btn-collapsed" className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-muted-foreground hover:text-destructive transition-colors">
                  <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Uitloggen</TooltipContent>
            </Tooltip>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
